import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileInvoice } from "react-icons/fa";
import { LoadingSpinner } from "../../../constants/const";
import { ApiFinance } from "../../funds/ApiFinance";
import {
  APPROVAL_STATUS,
  employeeNames,
  entryChipColor,
  entryTitle,
  formatRs,
  isDeductionLine,
  readErrorMessage,
  statusTheme,
} from "../../funds/expense/salaryTransfer/salaryTransferUtils";
import { GetLanguageString } from "../../helper/Components";
import { viewExpenseVoucherPdf } from "../../shared/receiptViewer";

const label = { fontSize: "10.5px", textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.4px" };
const value = { fontSize: "13px", fontWeight: 600, color: "#1F2937" };
const infoCard = (accent) => ({
  border: "1px solid #E5E7EB",
  borderLeft: `4px solid ${accent}`,
  borderRadius: "10px",
  padding: "10px 12px",
  height: "100%",
});

const formatDateTime = (val) => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const stepTheme = (step) => {
  if (step.statusCode === APPROVAL_STATUS.APPROVED) return { bg: "#ecfdf5", border: "#a7f3d0", dot: "#1ab394", mark: "✓" };
  if (step.statusCode === APPROVAL_STATUS.REJECTED || step.statusCode === APPROVAL_STATUS.CANCELLED)
    return { bg: "#fef2f2", border: "#fecaca", dot: "#ED5565", mark: "✕" };
  if (step.statusCode === APPROVAL_STATUS.HOLD) return { bg: "#fffbeb", border: "#fde68a", dot: "#f8ac59", mark: "‖" };
  return { bg: "#fff7ed", border: "#fed7aa", dot: "#f8ac59", mark: "•" };
};

/**
 * Read-only view of a salary transfer request: totals, linked approval chain and every quick
 * entry with its lines. Used by the Details modal and above the Take Action form.
 */
const SalaryTransferDetailsBody = ({ notificationId, onLoaded }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetSalaryTransferNotificationDetails(notificationId)
      .then((res) => {
        setDetail(res.data);
        if (onLoaded) onLoaded(res.data);
      })
      .catch((err) => setError(readErrorMessage(err, "Could not load the request.")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId]);

  if (loading) return LoadingSpinner();
  if (error || !detail) return <div className="bg-red-50" style={{ padding: "12px 16px" }}>{t(error)}</div>;

  const status = statusTheme(detail.statusCode, detail.isApproved);
  const entries = detail.entries || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "15px", fontWeight: 800, color: "#1F2937" }}>
          <GetLanguageString props="finance_salary_from_amanat" />
        </span>
        <span
          style={{
            background: status.bg,
            color: status.fg,
            border: `1px solid ${status.border}`,
            borderRadius: "999px",
            padding: "3px 12px",
            fontWeight: 700,
            fontSize: "12px",
          }}
        >
          <GetLanguageString props={status.key} />
        </span>
      </div>

      <div className="row">
        <div className="col-md-3 mb-2">
          <div style={infoCard("#1ab394")}>
            <div style={label}><GetLanguageString props="finance_salary_unit" /></div>
            <div style={value}>{detail.unitName}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div style={infoCard("#23c6c8")}>
            <div style={label}><GetLanguageString props="funds_date" /></div>
            <div style={value}>{(detail.date || "").replace(/-/g, "/")}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div style={infoCard("#2D8E7E")}>
            <div style={label}><GetLanguageString props="finance_salary_paid_from" /></div>
            <div style={value}>{detail.bankAccountName}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div style={{ border: "1px solid #A7F3D0", background: "#ECFDF5", borderRadius: "10px", padding: "10px 12px", height: "100%" }}>
            <div style={{ ...label, color: "#047857" }}>
              {t("finance_salary_total_payments", { count: entries.length, employees: detail.employeeCount })}
            </div>
            <div style={{ fontSize: "18px", fontWeight: 800, color: "#047857" }}>{formatRs(detail.amount)}</div>
          </div>
        </div>
      </div>

      <div style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "12px 14px" }}>
        <div style={{ ...label, fontWeight: 700, marginBottom: "10px" }}>
          <GetLanguageString props="finance_salary_approval_chain" />
        </div>
        <div className="row">
          {(detail.approvalChain || []).map((step) => {
            const theme = stepTheme(step);
            return (
              <div className="col-md-6 mb-2" key={step.notificationId}>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    height: "100%",
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      background: theme.dot,
                      color: "#fff",
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {theme.mark}
                  </span>
                  <div style={{ fontSize: "12px", color: "#374151", minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "#1F2937", fontSize: "12.5px" }}>
                      <GetLanguageString props={step.stage === "markaz" ? "finance_salary_markaz_approval" : "finance_salary_unit_approval"} />
                    </div>
                    <div>
                      <GetLanguageString props="finance_salary_submitted_by" />:{" "}
                      <span className="ur">{step.submittedByResponsibility}</span> · {formatDateTime(step.submittedAt)}
                    </div>
                    <div>
                      <GetLanguageString props="finance_salary_approver" />:{" "}
                      <span className="ur">{(step.approverResponsibilities || []).join("، ") || "—"}</span>
                    </div>
                    {step.actedAt ? (
                      <div>
                        <GetLanguageString props={statusTheme(step.statusCode).key} />:{" "}
                        <span className="ur">{step.actedByResponsibility}</span> · {formatDateTime(step.actedAt)}
                        {step.comments ? <div style={{ color: "#6b7280" }}>“{step.comments}”</div> : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: "11.5px", color: "#6b7280" }}>
          <GetLanguageString props="finance_salary_chain_hint" />
        </div>
      </div>

      {entries.map((entry, index) => (
        <div key={entry.quickEntryId} style={{ border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 14px",
              background: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                background: entryChipColor(index),
                color: "#fff",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {entryTitle(entry).charAt(0).toUpperCase()}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "#1F2937" }}>{entryTitle(entry)}</div>
              {entry.transNo > 0 && (
                <div style={{ fontSize: "11.5px", color: "#6b7280" }}>
                  <GetLanguageString props="funds_reference" /> {entry.reference}
                </div>
              )}
            </div>
            <span
              style={{
                background: "#EFF6FF",
                color: "#1d4ed8",
                border: "1px solid #BFDBFE",
                borderRadius: "6px",
                padding: "3px 10px",
                fontWeight: 800,
              }}
            >
              {formatRs(entry.amount)}
            </span>
            {entry.transNo > 0 && (
              <button
                type="button"
                className="btn btn-primary btn-xs no-drag"
                onClick={() => viewExpenseVoucherPdf({ transactionNo: entry.transNo, t }).catch(() => false)}
              >
                <FaFileInvoice style={{ marginRight: 4 }} />
                <GetLanguageString props="common_view_voucher" />
              </button>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ marginBottom: 0, fontSize: "12px" }}>
              <thead>
                <tr style={{ color: "#4B5563" }}>
                  {employeeNames(entry).length > 1 && (
                    <th><GetLanguageString props="finance_salary_employee" /></th>
                  )}
                  <th><GetLanguageString props="finance_salary_type" /></th>
                  <th><GetLanguageString props="finance_salary_line_memo" /></th>
                  <th style={{ textAlign: "right" }}><GetLanguageString props="funds_add_fund_amount" /></th>
                </tr>
              </thead>
              <tbody>
                {(entry.lines || []).map((line) => {
                  const isDeduction = isDeductionLine(line);
                  return (
                    <tr key={line.lineId}>
                      {employeeNames(entry).length > 1 && (
                        <td style={{ color: "#1F2937", fontWeight: 600 }}>{line.dimension2Name || "—"}</td>
                      )}
                      <td style={{ color: isDeduction ? "#b91c1c" : "#047857", fontWeight: 600 }}>
                        <GetLanguageString props={isDeduction ? "finance_salary_deduction" : "finance_salary_type_salary"} />
                      </td>
                      <td>{line.lineMemo}</td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: isDeduction ? "#b91c1c" : "#1F2937" }}>
                        {formatRs(line.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "10px 14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ ...label, fontWeight: 700 }}>
          <GetLanguageString props="transactions_memo" />
        </span>
        <span style={{ color: "#1F2937" }}>{detail.memo}</span>
      </div>
    </div>
  );
};

export default SalaryTransferDetailsBody;
